import { DateTime, Duration } from 'luxon'
import { timeRecordingService, userManagementService } from '@/api'
import router from '@/services/vuerouter/vueRouter'
import i18n from '@/services/vuei18n/vueI18n'
import { listenToLiveUpdates } from '@/services/liveUpdates/liveUpdates'

/**
 * Connects the web app to the ZeitFabrik desktop app, which shows the time clock in the menu bar or the tray. The
 * desktop app loads the web app in a window that stays open in the background, so it clocks in and out through the
 * web app as the signed-in user and needs no sign-in of its own.
 *
 * The desktop app is told the clock state of the organization last opened, and asks to clock in or out with the
 * events [TOGGLE_CLOCK] and [REFRESH_CLOCK].
 */

const TOGGLE_CLOCK = 'toggle-clock'
const REFRESH_CLOCK = 'refresh-clock'
const ORGANIZATION_KEY = 'desktopApp.organizationId'
const POLL_INTERVAL = 60000
// How long a failed query waits before it is subscribed again
const RECOVERY_DELAY = 30000
// How long the desktop app shows why clocking in or out failed
const ERROR_DURATION = 60000
const SIGNED_OUT_ROUTES = ['login', 'logout', 'register', 'reset-password', 'answer-invitation', 'email-confirmation',
  'attendance-monitor-admin-login', 'attendance-monitor-dashboard']

function connectDesktopApp (tauri = window.__TAURI__) {
  if (tauri == null) return

  let organizationId = null
  let queryRef = null
  let subscription = null
  let recoveryTimeout = null
  let clockState = null
  let error = null
  let errorTimeout = null
  let toggling = false

  function report () {
    tauri.core.invoke('set_clock_state', { update: { state: clockState, error } }).catch(() => {})
  }

  function toClockState (state) {
    if (state == null) return null
    return {
      clockedIn: state.clockedIn,
      clockedInSince: state.clockedInSince ? DateTime.fromISO(state.clockedInSince).toMillis() : null,
      clockedToday: Duration.fromISO(state.clockedTimeForCurrentDay).toMillis() || 0,
      fetchedAt: Date.now()
    }
  }

  function watch (id) {
    stop()
    organizationId = id
    localStorage.setItem(ORGANIZATION_KEY, id)
    queryRef = userManagementService.getMeWithCurrentClockState(
      { input: { organizationId: id } },
      { pollInterval: POLL_INTERVAL }
    )
    subscription = queryRef.subscribe({
      next: ({ data, loading }) => {
        if (data == null && loading) return
        clockState = toClockState(data?.me?.staff?.currentClockState)
        report()
      },
      error: () => {
        clockState = null
        report()
        recoveryTimeout = setTimeout(() => watch(id), RECOVERY_DELAY)
      }
    })
  }

  function stop () {
    clearTimeout(recoveryTimeout)
    subscription && subscription.unsubscribe()
    subscription = null
    queryRef = null
    organizationId = null
  }

  function refresh () {
    queryRef && queryRef.refetch().catch(() => {})
  }

  async function toggleClock () {
    if (organizationId == null || clockState == null || toggling) return
    toggling = true
    const input = { organizationId }
    try {
      const result = clockState.clockedIn
        ? await timeRecordingService.clockOutNow(input)
        : await timeRecordingService.clockInNow(input)
      error = result.errors ? i18n.t(`backend.${result.errors[0].message}`) : null
    } catch (e) {
      error = i18n.t('backend.networkError')
    } finally {
      toggling = false
    }
    clearTimeout(errorTimeout)
    if (error) {
      errorTimeout = setTimeout(() => {
        error = null
        report()
      }, ERROR_DURATION)
    }
    report()
    refresh()
  }

  router.afterEach(to => {
    if (SIGNED_OUT_ROUTES.includes(to.name)) {
      if (to.name === 'logout') localStorage.removeItem(ORGANIZATION_KEY)
      stop()
      clockState = null
      error = null
      report()
      return
    }
    const id = to.params.organizationId || (organizationId == null && localStorage.getItem(ORGANIZATION_KEY))
    if (id && id !== organizationId && userManagementService.isAuthenticated()) watch(id)
  })

  tauri.event.listen(TOGGLE_CLOCK, toggleClock)
  tauri.event.listen(REFRESH_CLOCK, refresh)
  listenToLiveUpdates(refresh)
}

export { connectDesktopApp }
